/**
 * feat-305 — fake streaming responder for `npm run start:fake` (ADR 0001 §10.7).
 *
 * Emits the SAME event sequence as the real contract (§1.4) on timers: a
 * `status` at ~400 ms, words every ~40 ms, each `footnote` before the first
 * `text` that carries its `[^n]` marker, then `done`. It honours the abort
 * signal (Stop) exactly like the real client: it resolves quietly and the
 * stored answer is marked `stopped` with exactly the text already released.
 *
 * An in-memory store keyed by context key makes restore work within a browser
 * session (it resets on reload).
 *
 * Scripted triggers (case-insensitive, first match wins), so every panel state
 * can be demonstrated:
 *
 *   "limit"                → 429-equivalent `limit_rate` rejection (no `start`)
 *   "exactly" / "say"      → the decline answer (shift content is private)
 *   "fail"                 → `start`, some text, then `error upstream`
 *   "privacy"              → some text, then `error privacy_blocked`
 *   "timeout"              → a lookup, then `error timeout`
 *   "lookups"              → six lookups, then `error limit_tool_calls`
 *   "slow"                 → a long answer (~15 s) to exercise Stop
 *   anything else          → a canned constructive answer with 2 footnotes
 *
 * After 20 user turns a conversation is full (`limit_conversation`), as on the
 * backend. URL switches: `?assistant=off` → config `enabled:false` (the
 * "control absent" acceptance state); `?assistant=restore-error` → restore fails.
 *
 * Copy follows the spec's tone guardrail: strengths first, one coaching
 * opportunity, evidence attached, no quotes, no pronouns for the server.
 */
import type {
  AssistantConfig,
  AssistantConversation,
  AssistantError,
  AssistantEvent,
  AssistantMessage,
  ChatRequest,
  Footnote,
  PageContext,
} from '../components/assistant/types';

export const FAKE_STATUS_DELAY_MS = 400;
export const FAKE_WORD_MS = 40;
const FAKE_SLOW_WORD_MS = 160;
const FAKE_RESTORE_MS = 350;
const MAX_TURNS = 20;

const urlFlag = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('assistant');
  } catch {
    return null;
  }
};

export function contextKey(ctx: PageContext): string {
  return `${ctx.page}:${ctx.server_id}`;
}

// ── store ────────────────────────────────────────────────────────────────────

const store = new Map<string, AssistantConversation>();
let seq = 0;
const nextId = (p: string) => `fake-${p}-${Date.now().toString(36)}-${(seq += 1)}`;
const nowIso = () => new Date().toISOString();

/** Test hook. */
export function __resetFakeAssistantStore(): void {
  store.clear();
}

/** Test/demo hook: seed a conversation for a context. */
export function __seedFakeConversation(ctx: PageContext, convo: AssistantConversation): void {
  store.set(contextKey(ctx), convo);
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

const wait = (ms: number, signal?: AbortSignal): Promise<boolean> =>
  new Promise((resolve) => {
    if (signal?.aborted) return resolve(false);
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      resolve(false);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

// ── config + restore ─────────────────────────────────────────────────────────

export async function fakeGetAssistantConfig(_tenantId: string): Promise<AssistantConfig> {
  return {
    enabled: urlFlag() !== 'off',
    pages: ['server_performance_history'],
  };
}

export async function fakeGetCurrentConversation(
  ctx: PageContext,
): Promise<AssistantConversation | null> {
  await wait(FAKE_RESTORE_MS);
  if (urlFlag() === 'restore-error') {
    const err: AssistantError = {
      code: 'network',
      message: 'Fake restore failure',
      retryable: true,
      retryAfterS: null,
    };
    throw err;
  }
  const c = store.get(contextKey(ctx));
  return c ? clone(c) : null;
}

// ── canned answers ───────────────────────────────────────────────────────────

interface Script {
  statuses: string[];
  text: string;
  footnotes: Footnote[];
  /** End with this error after releasing `text` (instead of `done`). */
  error?: { code: Extract<AssistantEvent, { type: 'error' }>['code']; message: string; retryable: boolean };
  wordMs?: number;
}

const fn = (n: number, label: string): Footnote => ({
  n,
  kind: 'shift',
  ref_id: `fake-shift-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
  label,
});

const HIGHLIGHTS: Script = {
  statuses: ['Looking at recent shifts…', 'Reviewing shift summaries…'],
  text:
    'Here are the highlights from August and September:\n\n' +
    '- **Greetings are a real strength.** Guests were greeted on 94% of visits across 21 shifts, up from 88% in June and July.\n' +
    '- **Best shift of the period:** a score of 91, the highest in the window.[^1]\n' +
    '- **Drink offers are climbing:** 4.1 per shift in September, up from 3.2 in August.\n\n' +
    'One opportunity to build on: Steak Shawarma upsells were offered less often on lunch shifts than at dinner.[^2] ' +
    'A quick role-play of the lunch pitch could carry the dinner habit over.',
  footnotes: [fn(1, 'Lunch shift, Sep 27'), fn(2, 'Lunch shift, Sep 3')],
};

const IMPROVE: Script = {
  statuses: ['Looking at recent shifts…', 'Checking what each metric measures…'],
  text:
    'The clearest opportunity is **offering a drink early at every table**.\n\n' +
    '- Drink offers were recorded on 41% of visits in September, down from 55% in August.\n' +
    '- The two lowest-scoring shifts were both dinners, and both had few drink offers.[^1][^2]\n\n' +
    'Greetings stayed strong all month, so this is about adding one habit, not fixing several. ' +
    'Practising a short drink line together before a dinner shift is a good place to start.',
  footnotes: [fn(1, 'Dinner shift, Sep 12'), fn(2, 'Dinner shift, Sep 24')],
};

const TREND: Script = {
  statuses: ['Looking at recent shifts…'],
  text:
    'Greeting scores have been **steady and high**:\n\n' +
    '1. July: 88% of visits greeted.\n' +
    '2. August: 92%.\n' +
    '3. September: 94%, with the best day on a lunch shift.[^1]\n\n' +
    'The one dip was a busy dinner shift at 79%.[^2] The data doesn\'t show *why* that shift dipped, so it may be worth asking about directly.',
  footnotes: [fn(1, 'Lunch shift, Sep 27'), fn(2, 'Dinner shift, Sep 12')],
};

const COACHING: Script = {
  statuses: ['Looking at recent shifts…', 'Reviewing shift summaries…'],
  text:
    'I\'d focus the next conversation on one thing: **an early drink offer at every table.**\n\n' +
    '- Start with the strength: greetings were recorded on 94% of visits this month.\n' +
    '- Then the opportunity: drink offers were missed on several dinner shifts.[^1][^2]\n' +
    '- Agree on one small practice goal for the next two weeks and check back on the drink metric.\n\n' +
    'Keeping it to one goal makes progress easy to see on this page.',
  footnotes: [fn(1, 'Dinner shift, Sep 12'), fn(2, 'Dinner shift, Sep 24')],
};

const DECLINE: Script = {
  statuses: ['Looking at recent shifts…'],
  text:
    'I can\'t share that. What\'s said on a shift, by the server or by guests, is private to the server, so I don\'t quote or describe it.\n\n' +
    'What I can tell you is how that shift scored. The lowest score in September was **58**,[^1] mostly because drink offers were recorded on 4 of 11 visits. ' +
    'If it helps, I can suggest a coaching focus built around that.',
  footnotes: [fn(1, 'Dinner shift, Sep 12')],
};

const FAIL: Script = {
  statuses: ['Looking at recent shifts…'],
  text: 'Over the last month, the clearest ',
  footnotes: [],
  error: { code: 'upstream', message: 'The assistant is unavailable right now.', retryable: true },
};

const PRIVACY: Script = {
  statuses: ['Reviewing shift summaries…'],
  text: 'The main coaching theme is the drink offer. ',
  footnotes: [],
  error: {
    code: 'privacy_blocked',
    message: 'This answer was stopped because it began to include private shift content.',
    retryable: true,
  },
};

const TIMEOUT: Script = {
  statuses: ['Looking at recent shifts…'],
  text: '',
  footnotes: [],
  error: { code: 'timeout', message: 'The answer took too long.', retryable: true },
};

const LOOKUPS: Script = {
  statuses: Array(6).fill('Looking at recent shifts…'),
  text: '',
  footnotes: [],
  error: {
    code: 'limit_tool_calls',
    message: 'This question needed more lookups than allowed. Try a narrower question.',
    retryable: true,
  },
};

const SLOW: Script = {
  ...COACHING,
  text: `${COACHING.text}\n\n${HIGHLIGHTS.text.replace('[^1]', '[^3]').replace('[^2]', '[^4]')}`,
  footnotes: [...COACHING.footnotes, fn(3, 'Lunch shift, Sep 27'), fn(4, 'Lunch shift, Sep 3')],
  wordMs: FAKE_SLOW_WORD_MS,
};

function pickScript(message: string): Script | 'limit' {
  const m = message.toLowerCase();
  if (m.includes('limit')) return 'limit';
  if (m.includes('exactly') || m.includes('say')) return DECLINE;
  if (m.includes('fail')) return FAIL;
  if (m.includes('privacy')) return PRIVACY;
  if (m.includes('timeout')) return TIMEOUT;
  if (m.includes('lookups')) return LOOKUPS;
  if (m.includes('slow')) return SLOW;
  if (m.includes('trend') || m.includes('greeting')) return TREND;
  if (m.includes('coaching') || m.includes('focus')) return COACHING;
  if (m.includes('improve')) return IMPROVE;
  return HIGHLIGHTS;
}

/** Split into word-sized deltas that keep their trailing whitespace. */
function toDeltas(text: string): string[] {
  return text.match(/\S+\s*|\s+/g) ?? [];
}

// ── the stream ───────────────────────────────────────────────────────────────

export async function fakeStreamChat(
  req: ChatRequest,
  onEvent: (e: AssistantEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted) return;
  const key = contextKey(req.page_context);
  const script = pickScript(req.message);

  let convo: AssistantConversation | undefined;
  if (req.conversation_id) {
    convo = store.get(key);
    if (!convo || convo.id !== req.conversation_id) {
      const err: AssistantError = { code: 'not_found', message: 'Not found', retryable: false, retryAfterS: null };
      throw err;
    }
  }

  if (script === 'limit') {
    await wait(250, signal);
    const err: AssistantError = {
      code: 'limit_rate',
      message: "You've asked a lot of questions in the last hour. Try again later.",
      retryable: true,
      retryAfterS: 840,
    };
    throw err;
  }
  if (convo && convo.messages.filter((m) => m.role === 'user').length >= MAX_TURNS) {
    const err: AssistantError = {
      code: 'limit_conversation',
      message: 'Start a new conversation.',
      retryable: false,
      retryAfterS: null,
    };
    throw err;
  }

  if (!convo) {
    convo = { id: nextId('c'), created_at: nowIso(), updated_at: nowIso(), messages: [] };
  }
  store.set(key, convo);
  const userMsg: AssistantMessage = {
    id: nextId('m'),
    role: 'user',
    body: req.message,
    status: 'complete',
    error_code: null,
    footnotes: [],
    created_at: nowIso(),
  };
  const asst: AssistantMessage = {
    id: nextId('m'),
    role: 'assistant',
    body: '',
    status: 'streaming',
    error_code: null,
    footnotes: [],
    created_at: nowIso(),
  };
  convo.messages.push(userMsg, asst);
  convo.updated_at = nowIso();

  const stopped = () => {
    asst.status = 'stopped';
  };

  onEvent({
    type: 'start',
    conversation_id: convo.id,
    user_message_id: userMsg.id,
    assistant_message_id: asst.id,
    contract: 1,
  });

  // Lookups.
  let first = true;
  for (const label of script.statuses) {
    if (!(await wait(first ? FAKE_STATUS_DELAY_MS : 600, signal))) return stopped();
    first = false;
    onEvent({ type: 'status', phase: 'looking_up', label });
  }
  if (!(await wait(500, signal))) return stopped();

  // Answer text.
  if (script.text) onEvent({ type: 'status', phase: 'writing', label: '' });
  const sent = new Set<number>();
  for (const delta of toDeltas(script.text)) {
    if (!(await wait(script.wordMs ?? FAKE_WORD_MS, signal))) return stopped();
    for (const m of Array.from(delta.matchAll(/\[\^(\d+)\]/g))) {
      const n = Number(m[1]);
      const f = script.footnotes.find((x) => x.n === n);
      if (f && !sent.has(n)) {
        sent.add(n);
        asst.footnotes.push(f);
        onEvent({ type: 'footnote', ...f });
      }
    }
    asst.body += delta;
    onEvent({ type: 'text', delta });
  }

  if (script.error) {
    asst.status = script.error.code === 'privacy_blocked' ? 'blocked' : 'error';
    asst.error_code = script.error.code;
    onEvent({
      type: 'error',
      code: script.error.code,
      message: script.error.message,
      retryable: script.error.retryable,
      retry_after_s: null,
      assistant_message_id: asst.id,
    });
    return;
  }
  asst.status = 'complete';
  convo.updated_at = nowIso();
  onEvent({
    type: 'done',
    assistant_message_id: asst.id,
    status: 'complete',
    footnotes: clone(asst.footnotes),
  });
}
